import {Trans} from '@lingui/macro';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {useInitialEffect} from '../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../libs/shared/helpers/src/types/asyncStatuses';
import {ExtendedError} from '../../../../libs/shared/helpers/src/types/customErrors';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {center, ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';
import {useUnmountCancelToken} from '../../../../srcCommon/helpers/hookHelpers';
import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {CancelError, CancelToken} from '../../../helpers/asyncCancelHelpers';
import {filterBySearchQuery} from '../../../helpers/models/schedulingLinkHelpers';
import {SchedulingLinkModel} from '../../../models/api/schedulingLinkModel';
import {HttpNetworkError, HttpTimeoutError} from '../../../services/http/httpError';
import {
  InterfaceEventsEnum,
  SchedulingLinksAccessPointsEnum,
  SchedulingSectionsEnum,
  trackInterfaceEvent,
} from '../../../services/tracking';
import {CalendarsPanelId, makeCalendarsPanelId} from '../../../store/panels/panelStateBase';
import {AsyncLoader} from '../../common/loaders/asyncLoader';
import {SideCalendarsHeader} from '../header/sideCalendarsHeader';
import {SchedulingLinksEmpty} from './schedulingLinksEmpty';
import {SchedulingLinksListItemContainer} from './schedulingLinksListItemContainer';
import {SchedulingLinksSearch} from './schedulingLinksSearch';
import {SchedulingLinksSearchEmpty} from './schedulingLinksSearchEmpty';
import {SchedulingLinkSettingsDialogContainer} from './settings/schedulingLinkSettingsDialogContainer';

/*
 * Props.
 */

interface SchedulingLinksListProps {
  panelId: CalendarsPanelId;
  schedulingLinks: ReadonlyArray<SchedulingLinkModel>;
  fetchSchedulingLinksAsync: (cancelToken: CancelToken) => Promise<void>;
  onBackClick: Handler;
}

/*
 * Style.
 */

const StyledSearchWrapperDiv = styled.div`
  padding: 0px 16px 16px 16px;
`;

const StyledAddSchedulingLinkButtonWrapperDiv = styled.div`
  display: flex;
  padding: 0px 16px 16px 16px;
`;

const StyledSeparatorDiv = styled.div`
  height: 1px;
  background-color: ${(p) => p.theme.greys.shade30};
`;

const StyledContentDiv = styled.div`
  height: 100%;
  overflow-y: scroll;
`;

const StyledLoaderWrapperDiv = styled.div`
  height: 100%;
  ${center()};
`;

const StyledQueryDiv = styled.div`
  padding: 8px 16px 4px 16px;
  font-size: ${fontSizes.large};
  font-weight: ${fontWeights.semibold};
  ${ellipsis()}
`;

/*
 * Component.
 */

export const SchedulingLinksList: FC<SchedulingLinksListProps> = (props) => {
  const {schedulingLinks, fetchSchedulingLinksAsync, onBackClick} = props;
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [status, setStatus] = useState<AsyncStatusesEnum>(AsyncStatusesEnum.LOADING);
  const [newlyCreatedSchedulingLinkId, setNewlyCreatedSchedulingLinkId] = useState<number>();
  const cancelToken = useUnmountCancelToken();

  const [newLinkDialogPanelId, setNewLinkDialogPanelId] = useState<CalendarsPanelId>();
  const onNewLinkClick = () => setNewLinkDialogPanelId(makeCalendarsPanelId());
  const onNewLinkDialogClose = () => setNewLinkDialogPanelId(undefined);

  // Log scheduling lists shown.
  useInitialEffect(() => {
    trackInterfaceEvent(InterfaceEventsEnum.SCHEDULING_P4_VIEW, {
      section: SchedulingSectionsEnum.SCHEDULING_LINK,
    });
  });

  const tryFetchSchedulingLinksAsync = async () => {
    setStatus(AsyncStatusesEnum.LOADING);
    try {
      await fetchSchedulingLinksAsync(cancelToken);
      setStatus(AsyncStatusesEnum.LOADED);
    } catch (error) {
      if (error instanceof CancelError) {
        return;
      }

      setStatus(AsyncStatusesEnum.FAILED);

      if (error instanceof HttpNetworkError || error instanceof HttpTimeoutError) {
        return;
      }

      reportToBugsnag(new ExtendedError('Failed to fetch scheduling links list.', error));
    }
  };

  useInitialEffect(() => {
    tryFetchSchedulingLinksAsync();
  });

  const areSchedulingLinksAvailable = status === AsyncStatusesEnum.LOADED || schedulingLinks.length > 0;
  return (
    <>
      <SideCalendarsHeader
        title={<Trans id="schedulinglinks-list-header">Scheduling links</Trans>}
        onBackClick={onBackClick}
      />
      <StyledSearchWrapperDiv>
        <SchedulingLinksSearch query={searchQuery} onQueryChange={setSearchQuery} />
      </StyledSearchWrapperDiv>
      {searchQuery && (
        <StyledQueryDiv>
          <Trans id="schedulinglinks-list-search-query">Results for &quot;{searchQuery}&quot;</Trans>
        </StyledQueryDiv>
      )}
      {!searchQuery && (
        <>
          <StyledAddSchedulingLinkButtonWrapperDiv>
            <Button size={VisualSizesEnum.MEDIUM} variant={ButtonVariantsEnum.LINK} onClick={onNewLinkClick}>
              <Trans id="sidepanel-schedulinglinks-add">+ New scheduling link</Trans>
            </Button>
          </StyledAddSchedulingLinkButtonWrapperDiv>
          <StyledSeparatorDiv />
        </>
      )}
      {!areSchedulingLinksAvailable && (
        <StyledLoaderWrapperDiv>
          <AsyncLoader status={status} onRetryClick={tryFetchSchedulingLinksAsync} />
        </StyledLoaderWrapperDiv>
      )}
      {areSchedulingLinksAvailable &&
        renderSchedulingLinks(props, searchQuery, schedulingLinks, newlyCreatedSchedulingLinkId)}
      {newLinkDialogPanelId && (
        <SchedulingLinkSettingsDialogContainer
          panelId={newLinkDialogPanelId}
          parentPanelId={props.panelId}
          onDialogClose={onNewLinkDialogClose}
          onSchedulingLinkCreated={setNewlyCreatedSchedulingLinkId}
          accessPoint={SchedulingLinksAccessPointsEnum.P4}
        />
      )}
    </>
  );
};

/*
 * Helpers.
 */

function renderSchedulingLinks(
  props: SchedulingLinksListProps,
  searchQuery: string,
  schedulingLinks: ReadonlyArray<SchedulingLinkModel>,
  newlyCreatedSchedulingLinkId?: number,
) {
  if (!searchQuery && schedulingLinks.length === 0) {
    return <SchedulingLinksEmpty />;
  }

  const schedulingLinksToRender = searchQuery
    ? filterBySearchQuery(schedulingLinks, searchQuery)
    : schedulingLinks;

  if (searchQuery && schedulingLinksToRender.length === 0) {
    return <SchedulingLinksSearchEmpty />;
  }

  return (
    <StyledContentDiv>
      {schedulingLinksToRender.map((schedulingLink) => (
        <SchedulingLinksListItemContainer
          key={schedulingLink.id}
          panelId={props.panelId}
          schedulingLink={schedulingLink}
          isNewlyCreated={schedulingLink.id === newlyCreatedSchedulingLinkId}
          accessPoint={SchedulingLinksAccessPointsEnum.P4}
        />
      ))}
    </StyledContentDiv>
  );
}
